import {
  Box,
  ButtonGroup,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  FoaPart,
  FrameworkAgreementType,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { FRAMEWORK_AGREEMENT_OPTIONS, isFrameworkEnabled } from "../../../../../common/procurement/publicProcurementOptions";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { FieldSaveError } from "./FieldSaveError";
import { displaySum } from "../../../../../common/formatting/displaySum";

interface Props {
  eventId: string;
}

export const FrameworkAgreementField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;

  const currentType = publicContext?.frameworkAgreement ?? FrameworkAgreementType.None;
  const enabled = isFrameworkEnabled(currentType);
  const maxParticipants = publicContext?.frameworkMaxParticipants ?? null;
  const saving = fieldStatus("publicContext.frameworkAgreement");
  const isSaving = saving === "saving" || saving === "pending";
  const maximumValue = publicContext?.frameworkMaximumValue?.value ?? null;
  const estimate = publicContext?.estimatedValue?.value ?? null;
  const currency = publicContext?.estimatedValue?.currency ?? publicContext?.frameworkMaximumValue?.currency ?? Currency.Nok;
  const maximumRequired =
    (publicContext?.regulation ?? Regulation.Foa) === Regulation.Foa && publicContext?.foaPart === FoaPart.PartIii;
  const [typedMaximum, setTypedMaximum] = useState<number | null | undefined>(undefined);
  const [maximumLeft, setMaximumLeft] = useState(false);
  const maximumShown = typedMaximum === undefined ? maximumValue : typedMaximum;
  const maximumBelowEstimate =
    (typedMaximum === undefined || maximumLeft) &&
    maximumShown !== null &&
    estimate !== null &&
    maximumShown < Number(estimate);
  const isSavingMaximum =
    fieldStatus("publicContext.frameworkMaximumValue") === "saving" ||
    fieldStatus("publicContext.frameworkMaximumValue") === "pending";
  const isSavingMax =
    fieldStatus("publicContext.frameworkMaxParticipants") === "saving" ||
    fieldStatus("publicContext.frameworkMaxParticipants") === "pending";

  const handleToggle = (checked: boolean) => {
    const defaultType = FRAMEWORK_AGREEMENT_OPTIONS[0].value;
    updateField({
      publicContext: {
        frameworkAgreement: checked ? defaultType : FrameworkAgreementType.None,
        frameworkMaxParticipants: checked ? maxParticipants : null,
      },
    });
  };

  const handleTypeChange = (value: string) => {
    updateField({ publicContext: { frameworkAgreement: value as FrameworkAgreementType } });
  };

  const handleMaximumValueChange = (value: number | null) => {
    setTypedMaximum(value);
    setMaximumLeft(false);
    updateField(
      { publicContext: { frameworkMaximumValue: value === null ? null : { value, currency } } },
      { debounceMs: 300 },
    );
  };

  const handleMaxParticipantsChange = (value: number | null) => {
    updateField({ publicContext: { frameworkMaxParticipants: value } }, { debounceMs: 300 });
  };

  return (
    <FormControl>
      <Flex align="center" justify="space-between" gap="4" mb={enabled ? "4" : "0"}>
        <FormLabel mb="0">{t("Framework agreement")}</FormLabel>
        <Flex align="center" gap="2">
          {isSaving && <Spinner size="xs" />}
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={enabled ? "teal" : "gray"}
              variant={enabled ? "solid" : "outline"}
              onClick={() => handleToggle(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!enabled ? "teal" : "gray"}
              variant={!enabled ? "solid" : "outline"}
              onClick={() => handleToggle(false)}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>
      <FieldSaveError eventId={eventId} path="publicContext.frameworkAgreement" />

      {enabled && (
        <Stack spacing="5">
          <FormControl as="fieldset">
            <FormLabel as="legend" fontSize="sm">
              {t("Call-off mechanism")}
            </FormLabel>
            <RadioOptionGroup
              options={FRAMEWORK_AGREEMENT_OPTIONS}
              value={currentType}
              onChange={handleTypeChange}
            />
          </FormControl>
          <Box>
            <PartiesInput
              saveError={<FieldSaveError eventId={eventId} path="publicContext.frameworkMaxParticipants" />}
              parties={maxParticipants}
              onPartiesChange={handleMaxParticipantsChange}
              isSaving={isSavingMax}
            />
          </Box>
          <FormControl isRequired={maximumRequired} isInvalid={maximumBelowEstimate}>
            <FormLabel htmlFor="framework-maximum-value" fontSize="sm">
              {t("Maximum value")} {isSavingMaximum && <Spinner size="xs" />}
            </FormLabel>
            <FieldSaveError eventId={eventId} path="publicContext.frameworkMaximumValue" />
            <InputGroup maxW={{ base: "full", sm: "280px" }}>
              <NumericInput
                id="framework-maximum-value"
                numericValue={maximumValue}
                onNumericChange={handleMaximumValueChange}
                onBlur={() => setMaximumLeft(true)}
                min={0}
              />
              <InputRightAddon>{currency}</InputRightAddon>
            </InputGroup>
            {maximumBelowEstimate ? (
              <FormErrorMessage>
                {t("The maximum value cannot be lower than the estimated value ({{amount}}).", {
                  amount: displaySum({ value: Number(estimate), currency }),
                })}
              </FormErrorMessage>
            ) : (
              <FormHelperText>
                {t(
                  "The highest total value that can be called off. It is stated in the notice, the call-offs together cannot exceed it, and it becomes the framework agreement's ceiling value.",
                )}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      )}
    </FormControl>
  );
};

const MAX_PARTIES = 1000;

export const parseParties = (text: string): { isValid: boolean; parties: number | null } => {
  const trimmed = text.trim();
  if (trimmed === "") return { isValid: true, parties: null };
  if (!/^[1-9]\d*$/.test(trimmed)) return { isValid: false, parties: null };
  const parties = Number(trimmed);
  return parties <= MAX_PARTIES ? { isValid: true, parties } : { isValid: false, parties: null };
};

const PartiesInput = ({
  parties,
  onPartiesChange,
  isSaving,
  saveError,
}: {
  parties: number | null;
  onPartiesChange: (parties: number | null) => void;
  isSaving: boolean;
  saveError: ReactNode;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(parties == null ? "" : String(parties));
  const { isValid } = parseParties(text);

  return (
    <FormControl isInvalid={!isValid}>
      <FormLabel htmlFor="framework-max-participants" fontSize="sm" mb="2">
        {t("Maximum number of suppliers")} {isSaving && <Spinner size="xs" />}
      </FormLabel>
      {saveError}
      <Input
        id="framework-max-participants"
        inputMode="numeric"
        maxW={{ base: "full", sm: "200px" }}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onPartiesChange(parseParties(e.target.value).parties);
        }}
      />
      <FormErrorMessage>{t("A whole number from 1 to {{max}}", { max: MAX_PARTIES })}</FormErrorMessage>
    </FormControl>
  );
};
